import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Upload, Link2, Loader2, UserRound, UserPlus, Wand2,
} from "lucide-react";

const ROLE_PRESETS = ["Host", "Co-host", "Special Guest", "Speaker"];
const CUSTOM = "__custom__";

function tempId() {
  return (crypto?.randomUUID?.() || `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`);
}

function Avatar({ photo, name, size = "h-12 w-12" }) {
  if (photo) {
    return <img src={photo} alt={name || "person"} className={`${size} rounded-full border border-border object-cover`} />;
  }
  return (
    <div className={`${size} flex items-center justify-center rounded-full border border-border bg-secondary text-muted-foreground`}>
      {name ? <span className="text-sm font-semibold">{name.trim().charAt(0).toUpperCase()}</span> : <UserRound className="h-5 w-5" />}
    </div>
  );
}

function MemberPicker({ onPick, index }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && members === null) {
      setLoading(true);
      api.get("/admin/members")
        .then((r) => setMembers(r.data || []))
        .catch(() => setMembers([]))
        .finally(() => setLoading(false));
    }
  }, [open, members]);

  const q = search.trim().toLowerCase();
  const filtered = (members || []).filter((m) => !q || (m.name || "").toLowerCase().includes(q)).slice(0, 50);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid={`person-link-member-${index}`}>
          <Link2 className="mr-1.5 h-3.5 w-3.5" /> Link member
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="border-b border-border p-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members…" className="h-9" data-testid={`person-member-search-${index}`} />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {loading && <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>}
          {!loading && filtered.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No members found.</p>}
          {!loading && filtered.map((m) => (
            <button
              key={m.user_id}
              type="button"
              onClick={() => { onPick(m); setOpen(false); setSearch(""); }}
              className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-secondary"
              data-testid={`person-member-option-${m.user_id}`}
            >
              <Avatar photo={m.avatar} name={m.name} size="h-8 w-8" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{m.name || "Unnamed"}</span>
                {(m.title || m.company) && <span className="block truncate text-xs text-muted-foreground">{[m.title, m.company].filter(Boolean).join(" · ")}</span>}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function EventPeopleEditor({ value, onChange }) {
  const people = Array.isArray(value) ? value : [];
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [illustratingIdx, setIllustratingIdx] = useState(null);
  const [artEnabled, setArtEnabled] = useState(false);
  useEffect(() => { api.get("/art/status").then((r) => setArtEnabled(!!r.data?.configured)).catch(() => {}); }, []);

  const update = (i, patch) => onChange(people.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const add = () => onChange([...people, { id: tempId(), role: "Special Guest", name: "", title: "", bio: "", photo: "", link: "", member_id: null }]);
  const remove = (i) => onChange(people.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= people.length) return;
    const next = [...people];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const illustrate = async (i) => {
    const p = people[i];
    const source = p.photo_original || p.photo;
    if (!source) return;
    setIllustratingIdx(i);
    try {
      const res = await api.post("/art/portrait", { photo_url: source, name: p.name || null });
      update(i, { photo: res.data.url, photo_original: source });
      toast.success("Illustrated portrait ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't illustrate that photo");
    } finally {
      setIllustratingIdx(null);
    }
  };

  const uploadPhoto = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "event-people");
      const res = await api.post("/storage/upload", fd);
      update(i, { photo: res.data.url });
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error("Upload failed — try a smaller image or paste a URL");
    } finally {
      setUploadingIdx(null);
    }
  };

  const linkMember = (i, m) => {
    const p = people[i] || {};
    update(i, {
      member_id: m.user_id,
      name: m.name || p.name,
      photo: m.avatar || p.photo,
      title: p.title || [m.title, m.company].filter(Boolean).join(", "),
    });
    toast.success(`Linked ${m.name}`);
  };

  return (
    <div className="space-y-3" data-testid="event-people-editor">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Hosts &amp; Guests</Label>
          <p className="text-xs text-muted-foreground">Showcase who's hosting and who's joining this event. Drag-free reorder with the arrows.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="event-people-add">
          <Plus className="mr-1.5 h-4 w-4" /> Add person
        </Button>
      </div>

      {people.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          No hosts or guests yet. Add a host and your special guest to make this event shine.
        </div>
      )}

      <div className="space-y-3">
        {people.map((p, i) => {
          const isCustomRole = !ROLE_PRESETS.includes(p.role);
          return (
            <div key={p.id || i} className="rounded-xl border border-border bg-secondary/40 p-3" data-testid={`event-person-${i}`}>
              <div className="flex items-start gap-3">
                <Avatar photo={p.photo} name={p.name} />
                <div className="min-w-0 flex-1 space-y-2.5">
                  {/* Row: role + reorder/remove */}
                  <div className="flex items-center gap-2">
                    <Select value={isCustomRole ? CUSTOM : p.role} onValueChange={(v) => update(i, { role: v === CUSTOM ? "" : v })}>
                      <SelectTrigger className="h-8 w-40" data-testid={`person-role-${i}`}><SelectValue placeholder="Role" /></SelectTrigger>
                      <SelectContent>
                        {ROLE_PRESETS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        <SelectItem value={CUSTOM}>Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {isCustomRole && (
                      <Input value={p.role} onChange={(e) => update(i, { role: e.target.value })} placeholder="Custom role" className="h-8 w-40" data-testid={`person-role-custom-${i}`} />
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === people.length - 1} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(i)} aria-label="Remove" data-testid={`person-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  <div className="grid gap-2.5 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input value={p.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Full name" className="mt-1 h-9" data-testid={`person-name-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Title / tagline</Label>
                      <Input value={p.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="e.g. Founder, Acme" className="mt-1 h-9" data-testid={`person-title-${i}`} />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Short bio</Label>
                    <Textarea value={p.bio || ""} onChange={(e) => update(i, { bio: e.target.value })} rows={2} placeholder="A sentence or two about this person" className="mt-1" data-testid={`person-bio-${i}`} />
                  </div>

                  <div className="grid gap-2.5 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Link (optional)</Label>
                      <Input value={p.link || ""} onChange={(e) => update(i, { link: e.target.value })} placeholder="https://…" className="mt-1 h-9" data-testid={`person-link-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Photo URL (optional)</Label>
                      <Input value={p.photo || ""} onChange={(e) => update(i, { photo: e.target.value })} placeholder="https://… or upload below" className="mt-1 h-9" data-testid={`person-photo-url-${i}`} />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <label className="cursor-pointer" data-testid={`person-upload-label-${i}`}>
                        {uploadingIdx === i ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                        Upload photo
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(i, e.target.files?.[0])} data-testid={`person-upload-input-${i}`} />
                      </label>
                    </Button>
                    <MemberPicker index={i} onPick={(m) => linkMember(i, m)} />
                    {artEnabled && p.photo && (
                      <Button type="button" variant="outline" size="sm" onClick={() => illustrate(i)} disabled={illustratingIdx === i} data-testid={`person-illustrate-${i}`}>
                        {illustratingIdx === i ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
                        {p.photo_original && p.photo !== p.photo_original ? "Re-illustrate" : "Illustrate"}
                      </Button>
                    )}
                    {p.photo_original && p.photo !== p.photo_original && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => update(i, { photo: p.photo_original })} data-testid={`person-use-original-${i}`}>
                        Use original photo
                      </Button>
                    )}
                    {p.member_id && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <UserPlus className="h-3.5 w-3.5 text-primary" /> Linked to a member
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EventPeopleEditor;
